import { createFileRoute } from "@tanstack/react-router";

import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { AmoHero } from "../components/amocrm/AmoHero";
import { AmoTasks } from "../components/amocrm/AmoTasks";
import { AmoWhy } from "../components/amocrm/AmoWhy";
import { LeadForm } from "../components/shared/LeadForm";
import { AmoWidgets } from "../components/amocrm/AmoWidgets";
import { AmoSteps } from "../components/amocrm/AmoSteps";
import { AmoCertificates } from "../components/amocrm/AmoCertificates";
import { AmoPricing } from "../components/amocrm/AmoPricing";
import { Faq } from "../components/shared/Faq";
import { buildMeta, breadcrumbJsonLd, serviceJsonLd, SITE_URL } from "../lib/site";

const title = "Внедрение и настройка amoCRM под ключ | HAY.agency";
const description =
  "Внедрение и настройка amoCRM: воронки продаж, автоматизация, интеграции, аналитика и обучение команды. Настроим CRM под процессы бизнеса.";

const faqItems = [
  {
    q: "Сколько стоит внедрение amoCRM?",
    a: "Работы по внедрению — от 49 000 ₽. Лицензии amoCRM оплачиваются отдельно и напрямую вендору. Итоговую смету фиксируем после разбора процессов.",
  },
  {
    q: "Сколько занимает настройка?",
    a: "Базовая настройка — от 2 недель, проекты с интеграциями и автоматизацией — от 3 до 5 недель. Срок зависит от количества воронок и внешних систем.",
  },
  {
    q: "Можно ли перенести действующую CRM?",
    a: "Да. Переносим сделки, контакты, компании и историю коммуникаций, сохраняя структуру данных. Перед переносом согласуем схему полей и этапов.",
  },
  {
    q: "Какие интеграции вы подключаете?",
    a: "Телефонию, почту, мессенджеры и соцсети, сайт и формы, 1С и «Мой склад», сквозную аналитику. Нестандартные сценарии реализуем через API и виджеты.",
  },
  {
    q: "Обучаете ли сотрудников?",
    a: "Да. Проводим обучение для менеджеров и руководителей, передаём регламенты и записи. После запуска сопровождаем команду и дорабатываем настройки.",
  },
];

export const Route = createFileRoute("/amocrm")({
  head: () => ({
    ...buildMeta({ title, description, path: "/amocrm", image: `${SITE_URL}/og/services/amocrm.webp`, imageWidth: 1200, imageHeight: 630, imageAlt: "Внедрение amoCRM — HAY.agency", locale: "ru_RU" }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/amocrm#webpage`,
              url: `${SITE_URL}/amocrm`,
              name: title,
              description,
              inLanguage: "ru-RU",
              isPartOf: { "@id": `${SITE_URL}/#website` },
              breadcrumb: { "@id": `${SITE_URL}/amocrm#breadcrumbs` },
              mainEntity: { "@id": `${SITE_URL}/amocrm#service` },
            },
            serviceJsonLd({
              name: "Внедрение и настройка amoCRM",
              description,
              path: "/amocrm",
              id: `${SITE_URL}/amocrm#service`,
              serviceType: "Внедрение и настройка amoCRM",
              providerId: `${SITE_URL}/#organization`,
              inGraph: true,
            }),
            breadcrumbJsonLd(
              [
                ["Главная", "/"],
                ["amoCRM", "/amocrm"],
              ],
              { id: `${SITE_URL}/amocrm#breadcrumbs`, inGraph: true },
            ),
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/amocrm#faq`,
              mainEntity: faqItems.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout breadcrumb="amoCRM" jsonLd={false}>
      <AmoHero />
      <AmoTasks />
      <AmoWhy />
      <LeadForm
        id="amo-quote"
        title={
          <>
            Узнайте стоимость{" "}
            <span className="text-display-muted">внедрения CRM</span> в ваш бизнес
          </>
        }
        lead="Проанализируем процессы, подберем конфигурацию и рассчитаем срок внедрения."
        submitLabel="Рассчитать стоимость"
      />
      <AmoWidgets />
      <AmoSteps />
      <AmoCertificates />
      <ClientsMarquee bare />
      <AmoPricing />
      <Faq
        compact
        title="Частые вопросы о внедрении amoCRM"
        lead="Стоимость, сроки, перенос данных и обучение — коротко."
        items={faqItems}
      />
      <LeadForm
        id="event"
        title={
          <>
            Готовы к <span className="text-display-muted">масштабированию?</span>
          </>
        }
        lead="Расскажите о задаче — предложим сценарий внедрения и покажем прототип воронки."
        submitLabel="Получить прототип"
        withSelect
        selectOptions={[
          "Внедрение amoCRM с нуля",
          "Доработка текущей CRM",
          "Интеграции и виджеты",
          "Аудит и обучение команды",
        ]}
      />
    </ServicePageLayout>
  );
}
